'use client';

import {
  buildUserBlockingFailureProperties,
  USER_BLOCKING_FAILURE_EVENT,
  type UserBlockingFailure,
} from '../../../lib/observability/user-blocking-failure';
import { browserWorkspaceForPath } from '../../../lib/observability/workspace-error-context';
import { isAccessDeniedError, isAuthLapseError } from '@/lib/error-utils';
import {
  sendUserBlockingFailureFromLoadedCode,
  type LoadedPostHogClient,
} from './user-blocking-failure-beacon';

/** One send per failure group per tab per minute; Discord dedupes the rest. */
const REPORT_THROTTLE_MS = 60_000;
const lastReportAt = new Map<string, number>();

/** Whether `alertKey` may send now; remembers it when it may. */
function claimReportSlot(alertKey: string, now: number): boolean {
  const last = lastReportAt.get(alertKey);
  if (last !== undefined && now - last < REPORT_THROTTLE_MS) return false;
  if (lastReportAt.size > 200) lastReportAt.clear();
  lastReportAt.set(alertKey, now);
  return true;
}

type PostHogBrowserClient =
  typeof import('../../../lib/observability/posthog-browser-client');
let browserClient: Promise<PostHogBrowserClient> | null = null;

/** Loaded on the first report, so importing this module costs nothing. */
function loadBrowserClient(): Promise<PostHogBrowserClient> {
  browserClient ??= import('../../../lib/observability/posthog-browser-client');
  return browserClient;
}

/** Test seam: forget the throttle between cases. */
export function resetUserBlockingFailureThrottle(): void {
  lastReportAt.clear();
}

/**
 * Reports a failure that stopped the user's work (#6012). Fire-and-forget:
 * it runs after the current task, loads the PostHog client lazily (so every
 * module that reports stays import-safe outside the browser), sends without
 * the batch queue so the #alerting destination sees it within seconds, and
 * never throws into or waits on the caller's UI path.
 */
export function reportUserBlockingFailure(
  failure: UserBlockingFailure & {
    /** The page the work started on, when the user may have left it since. */
    pathname?: string | null;
    /**
     * The caller showed the user a clear reason they can act on (an archived
     * task, a full drive), so this is a product state, not an alert. Only a
     * caller that shows such a reason may set it; it is never a default.
     */
    explainedToUser?: boolean;
    /**
     * An already-loaded posthog-js the caller imports. With it the report
     * sends synchronously, with no lazy import, falling back to a beacon:
     * for failures where no further chunk can load (#6155).
     */
    loadedClient?: LoadedPostHogClient | null;
  }
): void {
  if (typeof window === 'undefined' || failure.explainedToUser) return;
  try {
    const {
      pathname = window.location?.pathname,
      explainedToUser: _explainedToUser,
      loadedClient,
      ...rest
    } = failure;
    const properties = buildUserBlockingFailureProperties(rest, {
      pathname,
      origin: window.location?.origin,
      workspaceSlug: browserWorkspaceForPath(pathname)?.slug,
    });
    if (!claimReportSlot(properties.alert_key, Date.now())) return;
    if (loadedClient !== undefined) {
      sendUserBlockingFailureFromLoadedCode(properties, loadedClient);
      return;
    }
    window.setTimeout(() => {
      void loadBrowserClient()
        .then(({ ensurePostHogBrowserInitialized, posthog }) => {
          if (!ensurePostHogBrowserInitialized()) return;
          posthog.capture(USER_BLOCKING_FAILURE_EVENT, properties, {
            send_instantly: true,
          });
        })
        .catch(() => {
          // Alerting must never break the page it reports on.
        });
    }, 0);
  } catch {
    // Same: a reporting failure is never the user's problem.
  }
}

/** Runs `request`; a failure is reported (see above) and rethrown unchanged. */
export async function withUserBlockingFailureReport<T>(
  failure: Omit<UserBlockingFailure, 'error'>,
  request: () => Promise<T>
): Promise<T> {
  try {
    return await request();
  } catch (error) {
    reportUserBlockingFailure({ ...failure, error });
    throw error;
  }
}

/**
 * A crash an error boundary shows instead of the page. A tab out of date
 * with a deploy, a lapsed session or a page the user may not see recovers
 * on its own or is expected, so none of them alerts.
 */
export function reportPageCrash(
  error: unknown,
  { deploySkew = false }: { deploySkew?: boolean } = {}
): void {
  try {
    if (deploySkew || isAuthLapseError(error) || isAccessDeniedError(error))
      return;
  } catch {
    // An error shaped too oddly to classify is still a crash.
  }
  reportUserBlockingFailure({ surface: 'page_crash', error });
}
